import { type FC, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { Button, InputBase, Stack, Typography } from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import { useLazyQuery } from "@apollo/client";
import { useTranslation } from "@/shared/hooks";
import { formatCurrency } from "@/shared/utils";
import { PromocodeQuery } from "@/entities/promocodes";
import {
  ProductTypeType,
  PromocodeTypeType,
  type PromocodeQueryType,
  type PromocodeQueryVariablesType,
} from "@/types/graphql/site.generated";

type Promocode = NonNullable<PromocodeQueryType["promocode"]>;

const ERROR_CAUSES: Record<string, string> = {
  NOT_STARTED: "promocodeButton.promocodePeriodNotStartedError",
  EXPIRED: "promocodeButton.promocodePeriodExpiredError",
  PRODUCT_ALREADY_PURCHASED: "promocodeButton.productAlreadyPurchasedError",
  ONLY_ONCE: "promocodeButton.onlyOnceUsePromocodeError",
  LIMIT_REACHED: "promocodeButton.limitReachedError",
  NOT_FOUND: "promocodeButton.notFoundError",
  TRIAL_NOT_APPLICABLE: "promocodeButton.trialNotApplicableError",
  TRIAL_NOT_AVAILABLE: "promocodeButton.trialNotAvailableError",
};

// Порт референсного getHelperText (PromocodeButton.js:16-69): TRIAL — одна
// строка; DISCOUNT — «даёт …%» и/или «… сумму», для подписки с уточнением
// «на первый платёж»; discount и amount не взаимоисключающие.
function getHelperText(
  t: ReturnType<typeof useTranslation>["t"],
  promocode: Promocode | null,
  currency: string,
  productType: ProductTypeType,
): string | null {
  if (!promocode) return null;
  if (promocode.type === PromocodeTypeType.TrialType)
    return t("promocodeButton.givesTrial");

  const parts: string[] = [t("promocodeButton.gives")];
  const subscription = productType === ProductTypeType.SubscriptionType;
  if (promocode.discount) {
    parts.push(
      t(
        subscription
          ? "promocodeButton.discountFirstPayment"
          : "promocodeButton.discount",
        { discount: promocode.discount },
      ),
    );
  }
  if (promocode.amount) {
    parts.push(
      t(
        subscription
          ? "promocodeButton.amountFirstPayment"
          : "promocodeButton.amount",
        { amount: formatCurrency(promocode.amount, currency) },
      ),
    );
  }
  return parts.join(" ");
}

interface PromocodeButtonProps {
  productId: string;
  productType: ProductTypeType;
  currency: string;
  onCompleted: (promocode: { id: string }) => void;
  setLoading: (value: boolean) => void;
}

export const PromocodeButton: FC<PromocodeButtonProps> = ({
  productId,
  productType,
  currency,
  onCompleted,
  setLoading,
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const promocodeFromUrl =
    typeof router.query.promocode === "string" ? router.query.promocode : "";

  const [code, setCode] = useState(promocodeFromUrl);

  const [fetchPromocode, { data, error }] = useLazyQuery<
    PromocodeQueryType,
    PromocodeQueryVariablesType
  >(PromocodeQuery, {
    ssr: false,
    onCompleted(result) {
      setLoading(false);
      if (result.promocode) onCompleted(result.promocode);
    },
    onError() {
      setLoading(false);
    },
  });

  const promocode = data?.promocode ?? null;

  const onClickUse = () => {
    if (!code) return;
    setLoading(true);
    void fetchPromocode({ variables: { code, productId } });
  };

  // ?promocode=CODE — авто-применение (референс PromocodeButton.js:128-133).
  // Поле промокода теперь всегда развёрнуто (макет 1b), разворачивать нечего.
  useEffect(() => {
    if (promocodeFromUrl) {
      setCode(promocodeFromUrl);
      setLoading(true);
      void fetchPromocode({
        variables: { code: promocodeFromUrl, productId },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promocodeFromUrl]);

  let helperText = getHelperText(t, promocode, currency, productType);
  if (error) {
    const cause = error.graphQLErrors?.[0]?.extensions?.cause;
    helperText = t(
      (typeof cause === "string" && ERROR_CAUSES[cause]) ||
        "promocodeButton.error",
    );
  }

  return (
    <Stack spacing={0.5}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={(theme) => ({
          p: "4px 4px 4px 16px",
          borderRadius: "10px",
          backgroundColor: theme.palette.background.default,
          border: `1px solid ${
            error ? theme.palette.error.main : theme.palette.divider
          }`,
        })}
      >
        <InputBase
          placeholder={t("promocodeButton.label")}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          disabled={!!promocode}
          fullWidth
          sx={{ fontWeight: 600, fontSize: "0.9rem" }}
          inputProps={{ "aria-label": t("promocodeButton.label") }}
        />
        {promocode ? (
          <DoneIcon color="success" sx={{ mr: 1 }} />
        ) : (
          <Button
            variant="soft"
            size="small"
            onClick={onClickUse}
            disabled={!code}
          >
            {t("promocodeButton.activate")}
          </Button>
        )}
      </Stack>
      {helperText && (
        <Typography
          variant="caption"
          color={error ? "error" : "text.secondary"}
          sx={{ px: 0.5 }}
        >
          {helperText}
        </Typography>
      )}
    </Stack>
  );
};
